import React, { useEffect, useState } from "react";
import { FileText, Download, AlertCircle } from "lucide-react";
import { api, formatError } from "../lib/api.js";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function _fmtKB(bytes) {
  if (!bytes || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`;
}

/**
 * Reusable inline preview for a single attachment stored on a record via an
 * `*_attachment_id` field (e.g. PEC `quotation_attachment_id`, PCP
 * `alidata_quotation_attachment_id`). PDF and image previews are rendered
 * inline; other file types show a download-only fallback.
 *
 * Props:
 *   attachmentId  — the attachment id to fetch and render
 *   title         — heading text (default: "Orçamento anexado")
 *   testidPrefix  — prefix for data-testid attributes (default: "quotation-preview")
 */
export default function QuotationAttachmentPreview({
  attachmentId,
  title = "Orçamento anexado",
  testidPrefix = "quotation-preview",
}) {
  const [meta, setMeta] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!attachmentId) return;
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const res = await api.get(`/attachments/${attachmentId}/download`, { responseType: "blob" });
        if (cancelled) return;
        const blob = res.data;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        const cd = res.headers?.["content-disposition"] || "";
        const m = cd.match(/filename="?([^";]+)"?/i);
        setMeta({
          filename: m ? m[1] : "orcamento",
          content_type: blob.type || "application/octet-stream",
          size: blob.size || 0,
        });
      } catch (e) {
        if (!cancelled) setErr(formatError(e?.response?.data?.detail) || "Não foi possível carregar a pré-visualização.");
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachmentId]);

  if (!attachmentId) return null;

  const isPdf = (meta?.content_type || "").includes("pdf");
  const isImage = (meta?.content_type || "").startsWith("image/");
  const downloadHref = `${API_URL}/attachments/${attachmentId}/download`;

  return (
    <section data-testid={testidPrefix}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-heading text-sm font-bold text-[#1A1A2E] flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-[#1F3864]" />
          {title}
          {meta && (
            <span className="text-xs font-normal text-[#6C757D]" data-testid={`${testidPrefix}-meta`}>
              · {meta.filename} · {_fmtKB(meta.size)}
            </span>
          )}
        </h3>
        <a
          href={downloadHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-sm border border-[#1F3864]/30 text-[#1F3864] hover:bg-[#1F3864] hover:text-white transition"
          data-testid={`${testidPrefix}-download`}
        >
          <Download className="w-3 h-3" /> Descarregar
        </a>
      </div>
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        {err && (
          <div className="p-4 flex items-start gap-2 text-sm text-[#721C24] bg-[#F8D7DA]" data-testid={`${testidPrefix}-error`}>
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>{err}</div>
          </div>
        )}
        {!err && !blobUrl && (
          <div className="p-6 text-center text-sm text-[#6C757D]" data-testid={`${testidPrefix}-loading`}>
            A carregar pré-visualização…
          </div>
        )}
        {blobUrl && isPdf && (
          <embed src={blobUrl} type="application/pdf" className="w-full h-[480px] bg-white" data-testid={`${testidPrefix}-pdf`} />
        )}
        {blobUrl && isImage && (
          <img src={blobUrl} alt={title} className="w-full max-h-[480px] object-contain bg-white" data-testid={`${testidPrefix}-image`} />
        )}
        {blobUrl && !isPdf && !isImage && (
          <div className="p-4 text-sm text-[#6C757D]" data-testid={`${testidPrefix}-unsupported`}>
            Este tipo de ficheiro não suporta pré-visualização inline. Usa o botão <b>Descarregar</b> para abrir.
          </div>
        )}
      </div>
    </section>
  );
}
